import { inject } from '@angular/core';
import { HttpErrorResponse, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { OAuthService } from 'angular-oauth2-oidc';
import { EMPTY, catchError, throwError } from 'rxjs';
import { AuthService } from './auth.service';
import { environment } from '../../../environments/environment';

/**
 * Attaches the Bearer token and watches the session on every request.
 *
 * @ticket EB-4580
 * The implicit flow has no refresh — `getAccessToken()` keeps returning an
 * expired token. A token already expired before sending triggers the re-auth
 * instead of a request that can only come back 401 (`EMPTY`: the page is
 * navigating away). A 401 response is handed to `AuthService.handleUnauthorized`,
 * which re-authenticates only when the auth server confirms the token is dead;
 * the error is re-thrown either way so callers still see it. Auth-server calls
 * (check_token, userdetails, ssologout) are exempt — AuthService handles their
 * 401s itself, and a re-auth there would loop or break the logout.
 *
 * Only reads watch the session — GET/HEAD and the search POSTs (`searchUrl`
 * takes its Elasticsearch query as a POST body). A write never starts a re-auth:
 * the redirect would discard the form the operator is saving. It goes out even
 * with an expired token and fails with the backend's 401, so the caller shows
 * its error and the form stays; the next read or the tab re-focus check
 * re-authenticates.
 *
 * Contract: a read whose re-auth redirect starts completes WITHOUT emitting
 * (`EMPTY`). Before `authorized`, AuthService never redirects from here, so
 * bootstrap `firstValueFrom(http…)` calls cannot hit an `EmptyError`.
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const oauthService = inject(OAuthService);
  const authService = inject(AuthService);
  const token = oauthService.getAccessToken();

  if (!token) {
    return next(req);
  }

  const watchSession = !req.url.startsWith(environment.authUrl) && isRead(req);

  if (watchSession && !oauthService.hasValidAccessToken() && authService.handleSessionExpired('expired-before-request')) {
    return EMPTY;
  }

  return next(req.clone({ setHeaders: { Authorization: `Bearer ${token}` } })).pipe(
    catchError((err: unknown) => {
      if (watchSession && err instanceof HttpErrorResponse && err.status === 401) {
        authService.handleUnauthorized();
      }
      return throwError(() => err);
    }),
  );
};

/** Requests that change no data — only these may trigger a re-auth redirect (EB-4580). */
function isRead(req: HttpRequest<unknown>): boolean {
  if (req.method === 'GET' || req.method === 'HEAD') return true;
  return req.method === 'POST' && req.url.startsWith(environment.searchUrl);
}
